/**
 * 販売期間（custom.sale_start / sale_end）の切り替え。自作ファイル。
 *
 * サーバー（Liquid）は描画した瞬間の時刻で「販売前 / 販売中 / 販売終了」を決める。
 * ただしページはキャッシュされるうえ、開きっぱなしのページもあるため、
 * 描画後に時刻が境界を越えると表示が古くなる。それをブラウザ側で補正する。
 *
 * マーカー: snippets/em-sale-window.liquid が出力する <span hidden data-em-sale ...>
 *   data-em-sale-state  サーバーが描画した時点の状態（open / before / ended）
 *   data-em-sale-start  開始時刻（UNIX秒。0 は指定なし）
 *   data-em-sale-end    終了時刻（UNIX秒。0 は指定なし）
 *   data-em-sale-scope  'product'（商品ページ）か 'card'（コレクション等のカード）
 *
 * - 商品ページ: Shop Pay の表示などもサーバー側で決まるため、ボタンだけを切り替えず
 *   キャッシュを避けた再読み込みで描画し直す
 * - カード: カート追加ボタンの disabled と文言だけをその場で切り替える
 *
 * お客様の端末の時計がずれていると、その分だけ切り替わりがずれる。
 * 注文の可否を止めるものではない（URL直打ちのカート追加などは防げない）。
 */

const RELOAD_GUARD_KEY = 'em-sale-window-reload';
// setTimeout の上限（約24.8日）を超えないようにする
const MAX_TIMER_MS = 2 ** 31 - 1;

/** @param {number} start @param {number} end @param {number} nowSec */
function stateAt(start, end, nowSec) {
  if (start > 0 && nowSec < start) return 'before';
  if (end > 0 && nowSec >= end) return 'ended';
  return 'open';
}

/** ボタン内の文言（テキストノード）だけを差し替える。アイコンやチェックマークは触らない */
function setButtonText(button, text) {
  const node = Array.from(button.childNodes).find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
  if (node) {
    node.textContent = text;
  } else {
    button.append(document.createTextNode(text));
  }
}

function reloadOnce() {
  try {
    // 同じ状態で何度も再読み込みしないための保険
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (Date.now() - last < 10000) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage が使えない環境では保険なしで続行する
  }
  const url = new URL(window.location.href);
  url.searchParams.set('em_sale_check', String(Date.now()));
  window.location.replace(url.toString());
}

/** @param {HTMLElement} marker */
function applyCard(marker, state) {
  const root = marker.closest('quick-add-component');
  const button = root?.querySelector('button[name="add"]');
  if (!button) return;

  if (state === 'open') {
    button.disabled = false;
    setButtonText(button, marker.dataset.emSaleTextOpen || '');
  } else {
    button.disabled = true;
    setButtonText(button, state === 'before' ? marker.dataset.emSaleTextBefore : marker.dataset.emSaleTextEnded);
  }
  marker.dataset.emSaleState = state;
}

/** @param {HTMLElement} marker */
function check(marker) {
  const start = Number(marker.dataset.emSaleStart || 0);
  const end = Number(marker.dataset.emSaleEnd || 0);
  const current = marker.dataset.emSaleState;
  const actual = stateAt(start, end, Math.floor(Date.now() / 1000));

  if (actual !== current) {
    if (marker.dataset.emSaleScope === 'card') {
      applyCard(marker, actual);
    } else {
      reloadOnce();
      return;
    }
  }
  schedule(marker, start, end);
}

/** 次の境界（開始 or 終了）の時刻に、もう一度判定する */
function schedule(marker, start, end) {
  const nowSec = Math.floor(Date.now() / 1000);
  const next = [start, end].filter((t) => t > nowSec).sort((a, b) => a - b)[0];
  if (!next) return;
  const wait = Math.min((next - nowSec) * 1000 + 500, MAX_TIMER_MS);
  window.setTimeout(() => check(marker), wait);
}

const initialized = new WeakSet();

function init(root = document) {
  root.querySelectorAll('[data-em-sale]').forEach((marker) => {
    if (initialized.has(marker)) return;
    initialized.add(marker);
    check(marker);
  });
}

init();

// カードは無限スクロール・セクション再描画・モーダルで後から DOM に入るため、監視して初期化する
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.addedNodes.length) {
      init();
      return;
    }
  }
}).observe(document.body, { childList: true, subtree: true });
